import React, { useEffect, useRef, useState } from 'react'
import styles from './Schedules.module.css'
import o from '../../components/Overlay.module.css'
import rv from './Overlays.module.css'
import { apiJson } from '../../lib/api'
import { PendingButton, useSlow, inertClass, Failed } from '../../components/pending'
import { useChanged, useLayer } from '../../components/layers'
import type { Filters, ReportDef } from './model'
import {
  CADENCES,
  EMAIL_RE,
  EXPIRIES,
  coversText,
  existingLine,
  filterWords,
  recipientKey,
  sendTime,
  toRef,
  type Cadence,
  type Expiry,
  type Match,
  type Recipient,
  type Schedule,
} from './schedules'

// V4 "Schedule {report}" (handoff §7l.6). Made from a report as it stands, or
// opened from Reports home to edit one schedule's recipients, cadence and
// link. The recipient field searches before it accepts: two characters look
// up people and active groups, up to five, in an overlay that floats over
// the dialog so it never changes size under the cursor; with no match, Add
// takes the text only if it is an email address.

type User = Parameters<typeof apiJson>[0]

interface Props {
  user: User
  // The report's filter keys, for the words the configuration line uses.
  def: Pick<ReportDef, 'key' | 'name' | 'filters'>
  onClose: () => void
  onSaved: (s: Schedule, message: string) => void
  onFailed: (e: unknown) => void
  // Create: the configuration the report is showing, and the report's other
  // schedules for the "Already scheduled" block.
  body?: { date_mode: 'period' | 'season'; filters: Partial<Filters> }
  teamName?: string | null
  existing?: Schedule[]
  // Edit: the schedule as the list last read it.
  schedule?: Schedule
  // The workspace's operating time zone, null while unset (UTC).
  timezone: string | null
}

const MIN_QUERY = 2

export function ScheduleDialog({ user, def, onClose, onSaved, onFailed, body, teamName = null, existing = [], schedule, timezone }: Props) {
  const editing = !!schedule
  const [to, setTo] = useState<Recipient[]>(schedule?.recipients || [])
  const [cadence, setCadence] = useState<Cadence>(schedule?.cadence || 'daily')
  const [onlyActive, setOnlyActive] = useState(schedule ? schedule.only_if_activity : true)
  const [expiry, setExpiry] = useState<Expiry>(schedule?.link_expiry || '1 week')
  const [free, setFree] = useState('')
  const [matches, setMatches] = useState<Match[]>([])
  const [searched, setSearched] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const slow = useSlow(busy)
  const inputEl = useRef<HTMLInputElement>(null)

  // A layer over the report (h 425-458): Escape, the scrim and Cancel ask
  // first once anything has changed; nothing closes it while it saves.
  const panelEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged({ to: to.map(recipientKey), cadence, onlyActive, expiry, free })
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: dirty && !busy, el: panelEl, modal: true, focusFirst: true, busy })

  // The search, a beat after typing stops; an answer to an older query is
  // dropped. Already-added people and groups are left out by the server.
  const q = free.trim()
  const exclude = to.filter((r) => r.kind !== 'email').map((r) => (r as { id: string }).id)
  const excludeKey = exclude.join(',')
  useEffect(() => {
    if (q.length < MIN_QUERY) {
      setMatches([])
      setSearched('')
      return
    }
    let live = true
    const t = setTimeout(() => {
      const p = new URLSearchParams({ q })
      if (excludeKey) p.set('exclude', excludeKey)
      apiJson<{ matches: Match[] }>(user, `/api/report-schedules/recipients?${p}`)
        .then((r) => {
          if (!live) return
          setMatches(r.matches)
          setSearched(q)
        })
        .catch(() => live && (setMatches([]), setSearched(q)))
    }, 180)
    return () => {
      live = false
      clearTimeout(t)
    }
  }, [q, excludeKey, user])

  const add = (r: Recipient) => {
    setTo((xs) => (xs.some((x) => recipientKey(x) === recipientKey(r)) ? xs : xs.concat(r)))
    setFree('')
    setMatches([])
    setSearched('')
    setErr(null)
    inputEl.current?.focus()
  }
  const addFree = () => {
    if (!q) return setErr('Search a person or group, or type an email address')
    // A match wins: a name typed in full is the person, not an address.
    if (matches.length && searched === q) return add({ ...matches[0] })
    if (!EMAIL_RE.test(q)) return setErr('No person or group matches: enter a valid email address')
    add({ kind: 'email', email: q, label: q, sub: 'Email address' })
  }

  const showMatches = q.length >= MIN_QUERY && searched === q && matches.length > 0
  const showNoMatch = q.length >= MIN_QUERY && searched === q && matches.length === 0
  const words = editing ? filterWords(def.filters, schedule!.filters, schedule!.team_name) : filterWords(def.filters, body?.filters || {}, teamName)
  const dateMode = editing ? schedule!.date_mode : body?.date_mode || 'period'
  const config = [...words, `covering ${coversText(dateMode, cadence)}`].join(', ')

  const go = async () => {
    if (busy) return
    if (!to.length) return setErr('Add at least one recipient')
    setBusy(true)
    const payload = { cadence, only_if_activity: onlyActive, link_expiry: expiry, recipients: to.map(toRef) }
    try {
      if (editing) {
        const r = await apiJson<{ schedule: Schedule }>(user, `/api/report-schedules/${schedule!.id}`, { method: 'PUT', body: { version: schedule!.version, ...payload } })
        onSaved(r.schedule, 'Schedule updated')
      } else {
        const r = await apiJson<{ schedule: Schedule }>(user, '/api/report-schedules', { method: 'POST', body: { report_key: def.key, ...body, ...payload } })
        onSaved(r.schedule, `Scheduled. ${def.name} goes out at ${sendTime(timezone)}`)
      }
    } catch (e: any) {
      if (e?.body?.code === 'STALE' || e?.status === 404) return onFailed(e)
      setErr(e?.message || 'Could not save the schedule')
      setBusy(false)
    }
  }

  const pill = (on: boolean) => `${styles.pill} ${on ? styles.pillOn : ''}`

  return (
    <div className={o.scrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
      <div ref={panelEl} tabIndex={-1} className={`${o.modal} ${styles.dialog}`} role="dialog" aria-label={editing ? `Edit the ${def.name} schedule` : `Schedule ${def.name}`}>
        <div className={o.headText}>
          <h2 className={`${o.title} ${rv.viewTitle}`}>{editing ? `Edit the ${def.name} schedule` : `Schedule ${def.name}`}</h2>
          <p className={`${o.sub} ${rv.viewSub}`} data-testid="schedule-config">
            Recipients get an email with a link back to this exact report configuration, {config}, not a file attachment.
          </p>
          {!editing && existing.length > 0 && (
            <div className={styles.existing} data-testid="already-scheduled">
              <span className={styles.existingHead}>Already scheduled</span>
              {existing.map((s) => (
                <span key={s.id} className={styles.existingLine}>
                  {existingLine(s)}
                </span>
              ))}
              <span className={styles.existingNote}>Manage it under Scheduled delivery on Reports home.</span>
            </div>
          )}
        </div>

        <div className={`${styles.field} ${inertClass(busy, slow)}`}>
          <label className={styles.label} htmlFor="sched-recipient">
            Recipients
          </label>
          {to.length > 0 && (
            <div className={styles.chips}>
              {to.map((r) => (
                <span key={recipientKey(r)} className={styles.chip} title={r.sub}>
                  <span className={styles.chipName}>{r.label}</span>
                  <button type="button" className={styles.chipX} aria-label={`Remove ${r.label}`} onClick={() => setTo((xs) => xs.filter((x) => recipientKey(x) !== recipientKey(r)))}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#8c491a" strokeWidth="3" strokeLinecap="round" aria-hidden>
                      <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className={styles.searchRow}>
            <input
              ref={inputEl}
              id="sched-recipient"
              className={styles.input}
              value={free}
              maxLength={254}
              autoComplete="off"
              placeholder="Search people and groups, or type an email address"
              onChange={(e) => (setFree(e.target.value), setErr(null))}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addFree())}
            />
            <button type="button" className={styles.addBtn} onClick={addFree}>
              Add
            </button>
            {showMatches && (
              <div className={styles.overlay} role="listbox" aria-label="Matching people and groups">
                {matches.map((m) => (
                  <button key={`${m.kind}:${m.id}`} type="button" role="option" aria-selected={false} className={styles.match} onClick={() => add({ ...m })}>
                    <span className={styles.matchText}>
                      <span className={styles.matchName}>{m.label}</span>
                      <span className={styles.matchSub}>{m.sub}</span>
                    </span>
                    <span className={styles.matchKind}>{m.kind === 'person' ? 'Person' : 'Group'}</span>
                  </button>
                ))}
              </div>
            )}
            {showNoMatch && (
              <div className={`${styles.overlay} ${styles.noMatch}`} role="status">
                {EMAIL_RE.test(q) ? 'No person or group matches. Add sends to this email address.' : 'No person or group matches. Type a full email address to send outside CONTROL.'}
              </div>
            )}
          </div>
        </div>

        <div className={`${styles.field} ${inertClass(busy, slow)}`}>
          <span className={styles.label}>Cadence</span>
          <div className={styles.pills} role="radiogroup" aria-label="Cadence">
            {CADENCES.map((c) => (
              <button key={c.k} type="button" role="radio" aria-checked={cadence === c.k} className={pill(cadence === c.k)} onClick={() => setCadence(c.k)}>
                {c.label}
              </button>
            ))}
          </div>
          <div className={styles.note}>Every schedule sends at {sendTime(timezone)}, after the day's work is in.</div>
          <button type="button" role="checkbox" aria-checked={onlyActive} className={styles.check} onClick={() => setOnlyActive((x) => !x)}>
            <span className={`${styles.box} ${onlyActive ? styles.boxOn : ''}`}>
              {onlyActive && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="m5 13 4.5 4.5L19 7" />
                </svg>
              )}
            </span>
            <span className={styles.checkText}>Only send if there was activity in the period</span>
          </button>
        </div>

        <div className={`${styles.field} ${inertClass(busy, slow)}`}>
          <span className={styles.label}>Link expires after</span>
          <div className={styles.pills} role="radiogroup" aria-label="Link expires after">
            {EXPIRIES.map((x) => (
              <button key={x} type="button" role="radio" aria-checked={expiry === x} className={pill(expiry === x)} onClick={() => setExpiry(x)}>
                {x}
              </button>
            ))}
          </div>
          <div className={styles.note}>
            The link is an unguessable token, works without a CONTROL login so external recipients can open it, and grants read-only access to this report alone. It can be revoked at any time from Reports home. Three months is the maximum.
          </div>
        </div>

        <div className={`${o.foot} ${rv.viewFoot}`}>
          <button type="button" className={o.ghost} onClick={requestClose} disabled={busy}>
            Cancel
          </button>
          <Failed text={err} />
          <PendingButton type="button" className={o.primary} onClick={go} busy={busy} pending="Saving…">
            {editing ? 'Save changes' : 'Schedule it'}
          </PendingButton>
        </div>
      </div>
      {confirm}
    </div>
  )
}
